/**
 * API Response Normalization Utility
 * 
 * This module provides utilities for normalizing API responses to handle both
 * legacy direct array responses and new paginated response objects.
 * 
 * Background:
 * The backend API migrated from returning direct arrays `[...]` to paginated
 * response objects `{ items: [...], total: number, page: number }`. This utility
 * ensures frontend components can safely use array methods regardless of the
 * response format.
 */

/**
 * Interface for paginated API responses
 * 
 * @template T - The type of items in the array
 */
export interface PaginatedResponse<T> {
  /** Array of items in the current page */
  items: T[]
  /** Total number of items across all pages (optional) */
  total?: number
  /** Current page number (optional) */
  page?: number
  /** Number of items per page (optional) */
  pageSize?: number
  /** Total number of pages (optional) */
  totalPages?: number
}

/**
 * Normalizes API responses to ensure consistent array format
 * 
 * This function handles multiple response formats:
 * - Paginated objects: `{ items: [...] }` → extracts and returns the items array
 * - Direct arrays: `[...]` → returns unchanged for backward compatibility
 * - Null/undefined: returns empty array `[]` to prevent crashes
 * - Unexpected formats: returns empty array with console.error logging
 * 
 * @template T - The type of items in the array
 * @param data - The API response data to normalize
 * @returns An array of items, guaranteed to be a valid array
 * 
 * @example
 * // Paginated response
 * const data = { items: [{id: 1}, {id: 2}], total: 2 }
 * const normalized = normalizeApiResponse(data)
 * // Returns: [{id: 1}, {id: 2}]
 * 
 * @example
 * // Direct array (legacy format)
 * const data = [{id: 1}, {id: 2}]
 * const normalized = normalizeApiResponse(data)
 * // Returns: [{id: 1}, {id: 2}]
 * 
 * @example
 * // Null/undefined safety
 * const data = null
 * const normalized = normalizeApiResponse(data)
 * // Returns: []
 * 
 * @example
 * // Unexpected format
 * const data = { unexpected: 'format' }
 * const normalized = normalizeApiResponse(data)
 * // Returns: [] (with console.error)
 */
export function normalizeApiResponse<T = any>(data: any): T[] {
  // Handle null/undefined: return empty array to prevent crashes
  if (data === null || data === undefined) {
    return []
  }

  // Handle direct arrays: return unchanged for backward compatibility
  if (Array.isArray(data)) {
    return data
  }

  // Handle paginated objects: extract items array
  if (data && typeof data === 'object' && Array.isArray(data.items)) {
    return data.items
  }

  // Single object response (e.g. create/update/get) — return as-is
  return data
}
